import React, { useState, useEffect } from "react";
import Select from "react-select";
import { FaPlus, FaTrash, FaEdit, FaSearch, FaSpinner } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../../api";

const ExpensePage = () => {
    const [form, setForm] = useState({
        category_id: "",
        subcategory_id: "",
        vendor_id: "",
        title: "",
        amount: "",
        expense_date: new Date().toISOString().slice(0, 10),
        payment_mode: { value: "cash", label: "Cash" },
        description: "",
    });

    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");

    /* ================= Fetch ================= */

    const fetchCategories = async () => {
        try {
            const res = await api.get("/expense/category/list");
            const list = res.data?.data ?? [];
            setCategories(list.map(c => ({ value: c.id, label: c.name })));
        } catch {
            toast.error("Failed to load categories");
        }
    };

    const fetchSubcategories = async (category_id) => {
        if (!category_id) return setSubcategories([]);
        try {
            const res = await api.get(`/expense/subcategory/list?category_id=${category_id}`);
            const list = res.data?.data ?? [];
            setSubcategories(list.map(s => ({ value: s.id, label: s.name })));
        } catch {
            toast.error("Failed to load subcategories");
        }
    };

    const fetchVendors = async () => {
        try {
            const res = await api.get("/vendor/list");
            const list = res.data?.data ?? [];
            setVendors(list.map(v => ({ value: v.id, label: v.name })));
        } catch {
            toast.error("Failed to load vendors");
        }
    };

    const fetchExpenses = async () => {
        try {
            const res = await api.get("/expenses/list?limit=100");
            setExpenses(res.data?.data ?? []);
        } catch {
            toast.error("Failed to load expenses");
        }
    };

    useEffect(() => {
        fetchCategories();
        fetchVendors();
        fetchExpenses();
    }, []);

    useEffect(() => {
        fetchSubcategories(form.category_id);
    }, [form.category_id]);

    /* ================= Submit ================= */

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.category_id) return toast.error("Category required");
        if (!form.subcategory_id) return toast.error("Sub Category required");
        if (!form.title.trim()) return toast.error("Title required");
        if (!form.amount || Number(form.amount) <= 0)
            return toast.error("Valid amount required");

        const payload = {
            category_id: Number(form.category_id),
            subcategory_id: form.subcategory_id || null,
            vendor_id: form.vendor_id || null,
            title: form.title,
            amount: Number(form.amount),
            expense_date: form.expense_date,
            payment_mode: form.payment_mode?.value,
            description: form.description || null,
        };

        try {
            setLoading(true);
            const res = await api.post("/expenses/add", payload);
            if (res.data?.status) {
                toast.success("Expense added successfully");
                fetchExpenses();
                setForm({
                    category_id: "",
                    subcategory_id: "",
                    vendor_id: "",
                    title: "",
                    amount: "",
                    expense_date: new Date().toISOString().slice(0, 10),
                    payment_mode: { value: "cash", label: "Cash" },
                    description: "",
                });
            } else {
                toast.error("Failed to save expense");
            }
        } catch {
            toast.error("Network error");
        } finally {
            setLoading(false);
        }
    };

    const filteredExpenses = expenses.filter((it) =>
        it.title?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div style={page}>
            <ToastContainer position="top-right" theme="colored" />

            {/* ===== Header ===== */}
            <div style={header}>
                <div>
                    <div style={headerTitle}>Expense Dashboard</div>
                    <div style={headerSub}>
                        Manage and track your financial activity
                    </div>
                </div>

                <div style={summaryCard}>
                    ₹{expenses.reduce((a, b) => a + Number(b.amount || 0), 0).toLocaleString()}
                    <span style={summaryLabel}>Total Spent</span>
                </div>
            </div>

            <div style={mainGrid}>

                {/* ========== LEFT GLASS FORM ========== */}
                <div style={glassCard}>
                    <div style={formTitle}>Add Expense</div>

                    <form onSubmit={handleSubmit}>
                        <div style={formGrid}>

                            <Select
                                placeholder="Category"
                                options={categories}
                                isSearchable
                                isClearable
                                value={categories.find(x => x.value === form.category_id) || null}
                                onChange={(opt) =>
                                    setForm(s => ({ ...s, category_id: opt?.value || "" }))
                                }
                                styles={premiumSelect}
                            />

                            <Select
                                placeholder="Sub Category"
                                options={subcategories}
                                isSearchable
                                isClearable
                                value={subcategories.find(x => x.value === form.subcategory_id) || null}
                                onChange={(opt) =>
                                    setForm(s => ({ ...s, subcategory_id: opt?.value || "" }))
                                }
                                styles={premiumSelect}
                            />
                            <Select
                                placeholder="Vendor (Optional)"
                                options={vendors}
                                isSearchable
                                isClearable
                                value={vendors.find(x => x.value === form.vendor_id) || null}
                                onChange={(opt) =>
                                    setForm(s => ({ ...s, vendor_id: opt?.value || "" }))
                                }
                                styles={premiumSelect}
                            />
                            <input
                                type="text"
                                placeholder="Title"
                                value={form.title}
                                onChange={(e) =>
                                    setForm(s => ({ ...s, title: e.target.value }))
                                }
                                style={premiumInput}
                            />

                            <input
                                type="number"
                                placeholder="Amount"
                                value={form.amount}
                                onChange={(e) =>
                                    setForm(s => ({ ...s, amount: e.target.value }))
                                }
                                style={premiumInput}
                            />

                            <input
                                type="date"
                                value={form.expense_date}
                                onChange={(e) =>
                                    setForm(s => ({ ...s, expense_date: e.target.value }))
                                }
                                style={premiumInput}
                            />
                            <input
                                type="text"
                                placeholder="Description"
                                value={form.description}
                                onChange={(e) =>
                                    setForm(s => ({ ...s, description: e.target.value }))
                                }
                                style={premiumInput}
                            />
                            <Select
                                placeholder="Payment"
                                options={[
                                    { value: "cash", label: "Cash" },
                                    { value: "upi", label: "UPI" },
                                    { value: "bank", label: "Bank" },
                                    { value: "card", label: "Card" },
                                ]}
                                isSearchable
                                isClearable
                                value={form.payment_mode}
                                onChange={(opt) =>
                                    setForm(s => ({ ...s, payment_mode: opt }))
                                }
                                styles={premiumSelect}
                            />
                        </div>

                        <button style={premiumButton} disabled={loading}>
                            {loading ? <FaSpinner className="spin" /> : "Save Expense"}
                        </button>
                    </form>
                </div>

                {/* ========== RIGHT LIST ========== */}
                <div style={listContainer}>
                    <div style={stickySearch}>
                        <FaSearch style={{ color: "#94a3b8" }} />
                        <input
                            placeholder="Search expenses..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={searchInput}
                        />
                    </div>

                    <div style={expenseList}>
                        {filteredExpenses.map((item) => (
                            <div
                                key={item.id}
                                style={expenseCard}
                                onMouseEnter={(e) =>
                                    e.currentTarget.style.transform = "translateY(-3px)"
                                }
                                onMouseLeave={(e) =>
                                    e.currentTarget.style.transform = "translateY(0px)"
                                }
                            >
                                <div>
                                    <div style={expenseTitle}>{item.title}</div>
                                    <div style={expenseMeta}>
                                        {item.expense_date} • {item.category}
                                    </div>
                                </div>

                                <div style={amountStyle}>
                                    ₹{Number(item.amount).toLocaleString()}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

            </div>
        </div>
    );
};

/* ================= STYLES ================= */

const page = {
    minHeight: "100vh",
    padding: "10px",
    fontFamily: "Inter, sans-serif",
    background: "#f1f5f9",
    color: "#0f172a",
};

const header = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "40px",
    flexWrap: "wrap",
    gap: "20px",
};

const headerTitle = {
    fontSize: "28px",
    fontWeight: 700,
    color: "#0f172a",
};

const headerSub = {
    fontSize: "14px",
    color: "#64748b",
    marginTop: "4px",
};

const summaryCard = {
    background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
    padding: "20px 28px",
    borderRadius: "16px",
    fontSize: "22px",
    fontWeight: 700,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    color: "white",
    boxShadow: "0 10px 25px rgba(99,102,241,0.25)",
};

const summaryLabel = {
    fontSize: "12px",
    fontWeight: 400,
    marginTop: "6px",
    opacity: 0.8,
};

const mainGrid = {
    display: "grid",
    gridTemplateColumns: "420px 1fr",
    gap: "40px",
};

const glassCard = {
    background: "white",
    borderRadius: "20px",
    padding: "30px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 8px 30px rgba(0,0,0,0.05)",
};

const formTitle = {
    fontSize: "18px",
    fontWeight: 600,
    marginBottom: "20px",
    color: "#0f172a",
};

const formGrid = {
    display: "grid",
    gap: "16px",
    marginBottom: "20px",
};

const premiumInput = {
    height: "44px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    padding: "0 14px",
    color: "#0f172a",
    outline: "none",
};

const premiumButton = {
    width: "100%",
    height: "44px",
    borderRadius: "14px",
    border: "none",
    fontWeight: 600,
    background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
    color: "white",
    cursor: "pointer",
    boxShadow: "0 8px 20px rgba(99,102,241,0.25)",
};

const premiumSelect = {
    control: (base) => ({
        ...base,
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        minHeight: "44px",
        boxShadow: "none",
    }),
    singleValue: (base) => ({
        ...base,
        color: "#0f172a",
    }),
    menu: (base) => ({
        ...base,
        background: "white",
        borderRadius: "12px",
    }),
};

const listContainer = {
    display: "flex",
    flexDirection: "column",
};

const stickySearch = {
    position: "sticky",
    top: 0,
    display: "flex",
    gap: "10px",
    background: "white",
    padding: "14px 16px",
    borderRadius: "14px",
    marginBottom: "20px",
    alignItems: "center",
    border: "1px solid #e2e8f0",
    boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
};

const searchInput = {
    flex: 1,
    border: "none",
    outline: "none",
    background: "transparent",
    color: "#0f172a",
    fontSize: "14px",
};

const expenseList = {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    maxHeight: "600px",
    overflowY: "auto",
};

const expenseCard = {
    background: "white",
    padding: "20px",
    borderRadius: "16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    transition: "0.2s ease",
    cursor: "pointer",
    border: "1px solid #e2e8f0",
    boxShadow: "0 4px 15px rgba(0,0,0,0.04)",
};

const expenseTitle = {
    fontSize: "16px",
    fontWeight: 600,
    color: "#0f172a",
};

const expenseMeta = {
    fontSize: "12px",
    color: "#64748b",
    marginTop: "4px",
};

const amountStyle = {
    fontSize: "18px",
    fontWeight: 700,
    color: "#6366f1",
};

export default ExpensePage;